/**
 * ClinicOperationsDashboard — Coordinator Workspace (COORDINATOR-WORKSPACE-001)
 * =============================================================================
 *
 * Replaces the previous card-based dashboard with the dense, grid-first
 * operational workspace mandated by COORDINATOR-GRID-UX-001.
 *
 *   - Sticky header (12px text, compact)
 *   - Sticky filter chip row driven by /api/clinic/coordinator/summary
 *   - Single DataGrid (32px rows) per view — never cards
 *   - Right-side DetailDrawer on row click
 *   - 6 views (tabs): Cases · Requirements · Blockers · Overrides ·
 *     Assignments · History — all using the same primitives.
 *
 * Surfaces consume the read-only aggregator at /api/clinic/coordinator/*
 * and are accessible to both provider_admin and provider_coordinator
 * (both carry `case.view`).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Building2, RefreshCw, ExternalLink, ChevronDown, FileSpreadsheet } from 'lucide-react';

import { api } from '../../utils/api';
import { useClinicScope } from '../../hooks/useClinicScope';
import { useFriendlyNames } from '../../hooks/useFriendlyNames';
import MissingDocumentsView from './MissingDocumentsView';
import MissingConsentsView from './MissingConsentsView';
import ProfileRequestsLens from './ProfileRequestsLens';
import CoordinatorAttestationLens from './CoordinatorAttestationLens';
import {
  DataGrid,
  DetailDrawer,
  DrawerField,
  FilterChips,
  BulkBar,
  Pill,
} from '../../components/journey-ux';
import BulkLeadImport from './admin/BulkLeadImport';

/* -------------------------------------------------------------------- */
/*  Pure helpers                                                        */
/* -------------------------------------------------------------------- */

const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
    return d.toISOString().slice(0, 10);
  } catch {
    return '—';
  }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return '—';
  }
};

const numPill = (n, tone = 'rose') =>
  n > 0 ? (
    <Pill tone={tone} testId={`pill-num-${n}`}>{n}</Pill>
  ) : (
    <span className="text-slate-300">—</span>
  );

const STATUS_TONE = {
  ACTIVE: 'sky',
  IN_PROGRESS: 'amber',
  ON_TRACK: 'emerald',
  BLOCKED: 'rose',
  COMPLETED: 'emerald',
  CANCELED: 'slate',
  CANCELLED: 'slate',
};

const KIND_TONE = { REQUIREMENT: 'sky', TASK: 'violet' };

/* -------------------------------------------------------------------- */
/*  View definitions                                                    */
/* -------------------------------------------------------------------- */

const VIEWS = [
  { id: 'cases',            labelKey: 'provider.coordDash.navCases',             path: 'cases' },
  { id: 'missing-docs',     labelKey: 'provider.coordDash.navMissingDocs',       path: 'missing-documents' },
  { id: 'missing-consents', labelKey: 'provider.coordDash.navMissingConsents',   path: 'missing-consents' },
  { id: 'profile-requests', labelKey: 'provider.coordDash.navProfileRequests',   path: 'profile-requests' },
  { id: 'attestations',     labelKey: 'provider.coordDash.navAttestations',      path: 'attestations' },
  { id: 'escalations',      labelKey: 'provider.coordDash.navEscalations',       path: 'escalations' },
  { id: 'requirements',     labelKey: 'provider.coordDash.navRequirements',      path: 'requirements' },
  { id: 'blockers',         labelKey: 'provider.coordDash.navBlockers',          path: 'blockers' },
  { id: 'overrides',        labelKey: 'provider.coordDash.navOverrides',         path: 'overrides' },
  { id: 'assignments',      labelKey: 'provider.coordDash.navAssignments',       path: 'assignments' },
  { id: 'history',          labelKey: 'provider.coordDash.navHistory',           path: 'history' },
];

// TREATX-COORDINATOR-IA-CONSOLIDATION-01:
// Group the 11 existing views under 4 visible Level-2 parents. The
// underlying views (and their setView() switching, route, data, and
// permissions) are unchanged — only the navigation surface is
// regrouped to reduce horizontal clutter. Pattern mirrors the
// Provider Admin caret submenus (Offers / Treatments / Sales Team /
// Profile) — same interaction, same visuals, same active-state logic.
//
//   Cases ▾        → cases, missing-docs, missing-consents, profile-requests
//   Attestations   → attestations (no submenu)
//   Escalations ▾  → escalations, requirements, blockers, overrides
//   Assignments ▾  → assignments, history
const NAV_GROUPS = [
  {
    id: 'cases',
    labelKey: 'provider.coordDash.navCases',
    childIds: ['cases', 'missing-docs', 'missing-consents', 'profile-requests'],
  },
  {
    id: 'attestations',
    labelKey: 'provider.coordDash.navAttestations',
    childIds: [], // direct nav, no submenu
  },
  {
    id: 'escalations',
    labelKey: 'provider.coordDash.navEscalations',
    childIds: ['escalations', 'requirements', 'blockers', 'overrides'],
  },
  {
    id: 'assignments',
    labelKey: 'provider.coordDash.navAssignments',
    childIds: ['assignments', 'history'],
  },
];

// O(1) lookup of view metadata used by the regrouped nav.
const VIEW_BY_ID = VIEWS.reduce((acc, v) => { acc[v.id] = v; return acc; }, {});

// COA-02 — Cases is the operational inbox. Lightweight filter set only.
// Brief allowed: Search · Stage · Assigned To · Pending Items.
const BUCKET_CHIPS = [
  { id: 'all',         labelKey: 'provider.coordDash.bucketAll' },
  { id: 'mine',        labelKey: 'provider.coordDash.bucketMine' },
  { id: 'unassigned',  labelKey: 'provider.coordDash.bucketUnassigned' },
  { id: 'blocked',     labelKey: 'provider.coordDash.bucketBlocked' },
];

/* -------------------------------------------------------------------- */
/*  Cases — Assigned-To presentation                                    */
/* -------------------------------------------------------------------- */
const _shortId = (id) => (id ? `#${String(id).slice(0, 6)}` : null);
const renderAssignedTo = (r, nameLabel, t) => {
  const candidates = [
    [t('provider.coordDash.roleCoordinator'),  r.assigned_coordinator_id],
    [t('provider.coordDash.roleDoctor'),       r.assigned_doctor_id],
    [t('provider.coordDash.roleAfterSales'),   r.assigned_after_sales_id],
    [t('provider.coordDash.roleSales'),        r.assigned_sales_id],
  ].filter(([, v]) => !!v);
  if (candidates.length === 0) {
    return <span className="text-rose-500 text-[11px] uppercase tracking-wider">{t('provider.coordDash.unassigned')}</span>;
  }
  const [role, uid] = candidates[0];
  if (String(uid) === 'SYSTEM_POOL') {
    return (
      <span className="text-[11px] text-slate-500">
        {role} <span className="text-slate-400">· {t('provider.coordDash.pool')}</span>
      </span>
    );
  }
  const friendly = nameLabel ? nameLabel(uid) : null;
  return (
    <span className="text-[11px] text-slate-700">
      {role} <span className="font-medium text-slate-700">{friendly || _shortId(uid)}</span>
    </span>
  );
};

/* -------------------------------------------------------------------- */
/*  Column factories per view                                           */
/* -------------------------------------------------------------------- */

const casesColumns = (navigate, nameLabel, t) => [
  {
    id: 'case_id',
    header: t('provider.coordDash.colCase'),
    width: 'w-24',
    render: (r) => (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); navigate(`/clinic/cases/${r.case_id}/workflow`); }}
        className="font-mono text-[11px] text-sky-700 hover:underline"
        data-testid={`case-link-${r.case_id}`}
      >
        #{(r.case_id || '').slice(-6)}
      </button>
    ),
  },
  {
    id: 'patient',
    header: t('provider.coordDash.colPatient'),
    render: (r) => {
      const raw = r.patient_name || '';
      const isUuid = /^[0-9a-f-]{32,}$/i.test(raw);
      const label = isUuid ? `#${raw.slice(0, 8)}` : (raw || '—');
      return (
        <div className="flex items-center gap-1.5 min-w-0">
          {r.country && r.country !== '—' && (
            <span className="text-[10px] uppercase tracking-wider text-slate-500">{r.country}</span>
          )}
          <span className={`truncate ${isUuid ? 'font-mono text-slate-700 text-[11px]' : 'font-medium text-slate-800'}`}>
            {label}
          </span>
        </div>
      );
    },
  },
  {
    id: 'treatment',
    header: t('provider.coordDash.colTreatment'),
    render: (r) => <span className="text-slate-700 truncate">{r.treatment_name || r.treatment_slug || '—'}</span>,
  },
  {
    id: 'stage',
    header: t('provider.coordDash.colStage'),
    render: (r) =>
      r.journey_step
        ? <Pill tone="sky">{r.journey_step}</Pill>
        : <span className="text-slate-300">—</span>,
  },
  {
    id: 'pending_items',
    header: t('provider.coordDash.colPendingItems'),
    align: 'right',
    width: 'w-28',
    render: (r) => numPill(r.outstanding, 'amber'),
  },
  {
    id: 'assigned_to',
    header: t('provider.coordDash.colAssignedTo'),
    render: (r) => renderAssignedTo(r, nameLabel, t),
  },
  {
    id: 'last_activity',
    header: t('provider.coordDash.colLastActivity'),
    width: 'w-28',
    render: (r) => <span className="text-slate-500 text-[11px]">{fmtRel(r.last_activity)}</span>,
  },
  {
    id: 'created',
    header: t('provider.coordDash.colCreated'),
    width: 'w-24',
    render: (r) => <span className="text-slate-500 text-[11px] font-mono">{fmtDate(r.created_at)}</span>,
  },
  {
    id: 'open',
    header: '',
    width: 'w-24',
    render: (r) => (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); navigate(`/clinic/cases/${r.case_id}/workflow`); }}
        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-[11px] text-slate-700 font-medium"
        data-testid={`open-case-${r.case_id}`}
        title={t('provider.coordDash.openPlanningWorkspace')}
      >
        {t('provider.coordDash.openCase')} <ExternalLink className="h-3 w-3" />
      </button>
    ),
  },
];

const requirementsColumns = (t) => [
  {
    id: 'case',
    header: t('provider.coordDash.colCase'),
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24',
  },
  {
    id: 'patient',
    header: t('provider.coordDash.colPatient'),
    render: (r) => (
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase text-slate-500">{r.country || '—'}</span>
        <span className="text-slate-800 truncate">{r.patient_name || '—'}</span>
      </div>
    ),
  },
  { id: 'title', header: t('provider.coordDash.colRequirement'), render: (r) => <span className="text-slate-800 truncate">{r.title}</span> },
  {
    id: 'kind',
    header: t('provider.coordDash.colKind'),
    render: (r) => <Pill tone={KIND_TONE[r.kind] || 'slate'}>{r.requirement_type || r.kind}</Pill>,
  },
  {
    id: 'source',
    header: t('provider.coordDash.colSource'),
    render: (r) => <Pill tone={r.source === 'template' ? 'sky' : r.source === 'offer' ? 'amber' : r.source === 'campaign' ? 'violet' : 'rose'}>{r.source || '—'}</Pill>,
  },
  { id: 'role', header: t('provider.coordDash.colOwner'), render: (r) => <span className="text-slate-600">{r.default_role || '—'}</span> },
  {
    id: 'due',
    header: t('provider.coordDash.colDue'),
    render: (r) =>
      r.due_at ? (
        <span className={r.is_overdue ? 'text-rose-600 font-semibold' : 'text-slate-600'}>{fmtDate(r.due_at)}</span>
      ) : (
        <span className="text-slate-300">—</span>
      ),
  },
  {
    id: 'status',
    header: t('provider.coordDash.colStatus'),
    render: (r) =>
      r.satisfaction_status === 'satisfied' ? (
        <Pill tone="emerald">{t('provider.coordDash.statusSatisfied')}</Pill>
      ) : r.is_overdue ? (
        <Pill tone="rose">{t('provider.coordDash.statusOverdue')}</Pill>
      ) : (
        <Pill tone="amber">{t('provider.coordDash.statusPending')}</Pill>
      ),
  },
  {
    id: 'satisfied_at',
    header: t('provider.coordDash.colSatisfied'),
    render: (r) => (r.satisfied_at ? <span className="text-slate-500">{fmtRel(r.satisfied_at)}</span> : <span className="text-slate-300">—</span>),
  },
];

const blockersColumns = (t) => [
  {
    id: 'case',
    header: t('provider.coordDash.colCase'),
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24',
  },
  {
    id: 'patient',
    header: t('provider.coordDash.colPatient'),
    render: (r) => (
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase text-slate-500">{r.country || '—'}</span>
        <span className="text-slate-800 truncate">{r.patient_name || '—'}</span>
      </div>
    ),
  },
  { id: 'title', header: t('provider.coordDash.colItem'), render: (r) => <span className="text-slate-800 truncate">{r.title}</span> },
  { id: 'kind', header: t('provider.coordDash.colKind'), render: (r) => <Pill tone={KIND_TONE[r.kind] || 'slate'}>{r.requirement_type || r.kind}</Pill> },
  {
    id: 'reason',
    header: t('provider.coordDash.colReason'),
    render: (r) => (
      <div className="flex flex-wrap gap-1">
        {(r.reasons || []).map((reason) => (
          <Pill key={reason} tone="rose">{reason}</Pill>
        ))}
      </div>
    ),
  },
  { id: 'owner', header: t('provider.coordDash.colOwner'), render: (r) => <span className="text-slate-600">{r.owner || '—'}</span> },
  { id: 'due', header: t('provider.coordDash.colDue'), render: (r) => (r.due_at ? fmtDate(r.due_at) : '—') },
  {
    id: 'age',
    header: t('provider.coordDash.colAge'),
    align: 'right',
    render: (r) =>
      r.age_days > 0 ? (
        <Pill tone={r.age_days >= 3 ? 'rose' : 'amber'}>{r.age_days}d</Pill>
      ) : (
        <span className="text-slate-300">—</span>
      ),
  },
];

const overridesColumns = (nameLabel, t) => [
  {
    id: 'case',
    header: t('provider.coordDash.colCase'),
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24',
  },
  { id: 'treatment', header: t('provider.coordDash.colTreatment'), render: (r) => <span className="text-slate-700">{r.treatment_slug || '—'}</span> },
  { id: 'op', header: t('provider.coordDash.colOperation'), render: (r) => <Pill tone="violet">{r.op}</Pill> },
  {
    id: 'args',
    header: t('provider.coordDash.colArgs'),
    render: (r) => (
      <span className="font-mono text-[10.5px] text-slate-500 truncate" title={JSON.stringify(r.args || {})}>
        {JSON.stringify(r.args || {}).slice(0, 80)}
      </span>
    ),
  },
  { id: 'actor', header: t('provider.coordDash.colActor'), render: (r) => <span className="text-slate-700 text-[11px]">{nameLabel ? nameLabel(r.actor) : (r.actor || '').slice(0, 12)}</span> },
  { id: 'created_at', header: t('provider.coordDash.colWhen'), render: (r) => <span className="text-slate-500">{fmtRel(r.created_at)}</span> },
  {
    id: 'status',
    header: t('provider.coordDash.colStatus'),
    render: (r) => (r.undone ? <Pill tone="slate">{t('provider.coordDash.statusUndone')}</Pill> : <Pill tone="emerald">{t('provider.coordDash.statusActive')}</Pill>),
  },
];

const assignmentsColumns = (nameLabel, t) => [
  {
    id: 'assignee',
    header: t('provider.coordDash.colAssignee'),
    render: (r) =>
      r.is_unassigned ? (
        <span className="text-rose-600 font-medium">{t('provider.coordDash.unassigned')}</span>
      ) : (
        <span className="text-[11px] text-slate-700">
          {nameLabel ? nameLabel(r.assignee_id) : (r.assignee_id || '').slice(0, 14)}
        </span>
      ),
  },
  { id: 'role', header: t('provider.coordDash.colRole'), render: (r) => <Pill tone="sky">{r.role}</Pill> },
  { id: 'open_cases', header: t('provider.coordDash.colOpenCases'), align: 'right', render: (r) => <span className="font-semibold text-slate-800">{r.open_cases}</span> },
  { id: 'blocked',    header: t('provider.coordDash.colBlocked'),    align: 'right', render: (r) => numPill(r.blocked, 'rose') },
  { id: 'overdue',    header: t('provider.coordDash.colOverdue'),    align: 'right', render: (r) => numPill(r.overdue, 'rose') },
  { id: 'outstanding', header: t('provider.coordDash.colOutstanding'), align: 'right', render: (r) => <span className="text-slate-600">{r.outstanding_items}</span> },
];

const historyColumns = (nameLabel, t) => [
  {
    id: 'case',
    header: t('provider.coordDash.colCase'),
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24',
  },
  {
    id: 'action',
    header: t('provider.coordDash.colAction'),
    render: (r) => (
      <Pill
        tone={
          r.action?.startsWith('override-undo') ? 'slate'
          : r.action?.startsWith('override') ? 'violet'
          : r.action === 'rematerialize' ? 'amber'
          : 'sky'
        }
      >
        {r.action || '—'}
      </Pill>
    ),
  },
  { id: 'actor', header: t('provider.coordDash.colActor'), render: (r) => <span className="text-slate-700 text-[11px]">{nameLabel ? nameLabel(r.actor) : (r.actor || '').slice(0, 18)}</span> },
  {
    id: 'detail',
    header: t('provider.coordDash.colDetail'),
    render: (r) => (
      <span className="font-mono text-[10.5px] text-slate-500 truncate" title={JSON.stringify(r.after || {})}>
        {JSON.stringify(r.after || {}).slice(0, 80)}
      </span>
    ),
  },
  { id: 'when', header: t('provider.coordDash.colWhen'), render: (r) => <span className="text-slate-500">{fmtRel(r.created_at)}</span> },
];

// COA-10 G3 — Escalations lens columns. Reads from /clinic/coordinator/escalations.
const escalationsColumns = (nameLabel, t) => [
  {
    id: 'case',
    header: t('provider.coordDash.colCase'),
    render: (r) => <span className="font-mono text-[11px] text-slate-700">#{(r.case_id || '').slice(-6)}</span>,
    width: 'w-24',
  },
  {
    id: 'patient',
    header: t('provider.coordDash.colPatient'),
    render: (r) => <span className="text-slate-800 truncate">{r.patient_name || '—'}</span>,
  },
  { id: 'treatment', header: t('provider.coordDash.colTreatment'), render: (r) => <span className="text-slate-700">{r.treatment_name || r.treatment_slug || '—'}</span> },
  {
    id: 'item',
    header: t('provider.coordDash.colItem'),
    render: (r) => (
      <span className="inline-flex items-center gap-1.5 text-slate-800 truncate">
        {r.item_name}
        <Pill tone="rose" testId={`escalations-badge-${r.row_id}`}>{t('provider.coordDash.statusEscalated')}</Pill>
      </span>
    ),
  },
  {
    id: 'source',
    header: t('provider.coordDash.colSource'),
    render: (r) => <Pill tone={r.source === 'template' ? 'sky' : r.source === 'offer' ? 'amber' : r.source === 'campaign' ? 'violet' : 'rose'}>{r.source || '—'}</Pill>,
  },
  {
    id: 'role',
    header: t('provider.coordDash.colOwner'),
    render: (r) => <span className="text-slate-600 text-[11px]">{r.default_role || '—'}</span>,
  },
  {
    id: 'escalated_by',
    header: t('provider.coordDash.colEscalatedBy'),
    render: (r) => <span className="text-slate-700 text-[11px]">{nameLabel ? nameLabel(r.escalated_by) : ((r.escalated_by || '').slice(0, 12) || '—')}</span>,
  },
  {
    id: 'age',
    header: t('provider.coordDash.colAge'),
    align: 'right',
    width: 'w-24',
    render: (r) => {
      const h = r.age_hours || 0;
      const tone = h >= 48 ? 'rose' : h >= 24 ? 'amber' : 'slate';
      const lbl = h >= 24 ? `${Math.round(h / 24)}d` : `${h}h`;
      return <Pill tone={tone}>{lbl}</Pill>;
    },
  },
  {
    id: 'status',
    header: t('provider.coordDash.colStatus'),
    render: (r) => r.is_satisfied ? <Pill tone="emerald">{t('provider.coordDash.statusSatisfied')}</Pill> : r.is_overdue ? <Pill tone="rose">{t('provider.coordDash.statusOverdue')}</Pill> : <Pill tone="amber">{t('provider.coordDash.statusActive')}</Pill>,
  },
];

const ROW_KEY_FOR = {
  cases:        (r) => r.case_id,
  requirements: (r) => `${r.case_id}::${r.row_id}`,
  blockers:     (r) => `${r.case_id}::${r.row_id}`,
  escalations:  (r) => `${r.case_id}::${r.row_id}`,
  overrides:    (r) => r.override_id,
  assignments:  (r) => `${r.assignee_id || 'unassigned'}::${r.role}`,
  history:      (r) => r.revision_id,
};

/* -------------------------------------------------------------------- */
/*  Main component                                                      */
/* -------------------------------------------------------------------- */

export default function ClinicOperationsDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { clinicId, userName } = useClinicScope();
  const { label: nameLabel, seed: seedNames } = useFriendlyNames();
  // TREATX-PROVIDER-ADMIN-OPERATIONAL-SUMMARY-DEEP-LINKS-01:
  // Honour an incoming `initialView` passed via React Router location
  // state (set by AdminOperationalSummaryCard tiles on `/clinic/admin`).
  // Only seeds the initial tab — the existing setView() tab mechanism is
  // unchanged. Falls back to 'cases' on direct loads / reloads.
  const incomingView = location.state?.initialView;
  const seededView = VIEWS.some((v) => v.id === incomingView) ? incomingView : 'cases';
  const [view, setView] = useState(seededView);
  const [bucket, setBucket] = useState('all');
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeRow, setActiveRow] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  // BULK-LEAD-IMPORT-V1 — a Coordinator holds `lead.manage`, so the staged
  // import workspace is reachable from their own workspace header too.
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [importReps, setImportReps] = useState([]);
  useEffect(() => {
    api.get('/clinic/team')
      .then(({ data }) => setImportReps(
        (data.members || []).filter((m) => m.role === 'provider_sales_rep')))
      .catch(() => {});
  }, []);
  // COA-02 — Cases inbox search + stage filter (client-side, lightweight).
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('__all__');

  // TREATX-COORDINATOR-IA-CONSOLIDATION-01:
  // Tracks the currently open caret submenu id ('cases' | 'escalations'
  // | 'assignments' | null). Mirrors Provider Admin dashboard pattern.
  const [openNavSubmenu, setOpenNavSubmenu] = useState(null);
  const navContainerRef = useRef(null);
  useEffect(() => {
    if (!openNavSubmenu) return undefined;
    const handleClickOutside = (event) => {
      if (navContainerRef.current && !navContainerRef.current.contains(event.target)) {
        setOpenNavSubmenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openNavSubmenu]);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/clinic/coordinator/summary');
      if (data?.success) setSummary(data);
    } catch {
      setSummary(null);
    }
  }, []);

  const fetchView = useCallback(async () => {
    if (!view) return;
    // COA-05 / COA-06 — missing-docs and missing-consents have their own
    // self-managed sub-components; skip the generic fetch so we don't double-load.
    if (view === 'missing-docs' || view === 'missing-consents' || view === 'profile-requests' || view === 'attestations') {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setSelectedIds(new Set());
    setActiveRow(null);
    try {
      const path = VIEWS.find((v) => v.id === view)?.path;
      const query = view === 'cases' && bucket !== 'all' ? `?bucket=${bucket}` : '';
      const { data } = await api.get(`/clinic/coordinator/${path}${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      // COA-10 G2 — preload server-resolved display names if the endpoint
      // returned a `people` block (escalations does).
      if (data?.people) seedNames(data.people);
      // Pre-warm assignee names for the Cases view too.
      if (view === 'cases' && Array.isArray(data?.items)) {
        const ids = [];
        data.items.forEach((c) => {
          ['assigned_coordinator_id', 'assigned_doctor_id', 'assigned_after_sales_id', 'assigned_sales_id'].forEach((k) => {
            if (c[k]) ids.push(c[k]);
          });
        });
        if (ids.length) {
          // Trigger lazy fetch via the resolver (label() queues unknown ids).
          ids.forEach((id) => nameLabel(id));
        }
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [view, bucket, nameLabel, seedNames]);

  useEffect(() => {
    if (!clinicId) return;
    fetchSummary();
    fetchView();
  }, [clinicId, fetchSummary, fetchView, reloadTick]);

  /* -------------------- columns + chips per view -------------------- */
  const columns = useMemo(() => {
    if (view === 'cases')        return casesColumns(navigate, nameLabel, t);
    if (view === 'requirements') return requirementsColumns(t);
    if (view === 'blockers')     return blockersColumns(t);
    if (view === 'escalations')  return escalationsColumns(nameLabel, t);
    if (view === 'overrides')    return overridesColumns(nameLabel, t);
    if (view === 'assignments')  return assignmentsColumns(nameLabel, t);
    return historyColumns(nameLabel, t);
  }, [view, navigate, nameLabel, t]);

  const chips = useMemo(() => {
    if (view !== 'cases') return [];
    const localized = BUCKET_CHIPS.map((c) => ({ ...c, label: t(c.labelKey) }));
    if (!summary?.buckets) return localized;
    return localized.map((c) => ({ ...c, count: summary.buckets[c.id] ?? 0 }));
  }, [summary, view, t]);

  // COA-02 — derived stage list + filtered cases for the inbox.
  const stageOptions = useMemo(() => {
    if (view !== 'cases') return [];
    const set = new Set();
    items.forEach((r) => { if (r.journey_step) set.add(r.journey_step); });
    return Array.from(set).sort();
  }, [items, view]);

  const visibleItems = useMemo(() => {
    if (view !== 'cases') return items;
    const q = searchTerm.trim().toLowerCase();
    return items.filter((r) => {
      if (stageFilter !== '__all__' && (r.journey_step || '') !== stageFilter) return false;
      if (!q) return true;
      const hay = [
        r.case_id, r.patient_name, r.treatment_name, r.treatment_slug, r.journey_step,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [items, view, searchTerm, stageFilter]);

  /* -------------------- row interactions ---------------------------- */
  const rowKey = ROW_KEY_FOR[view];
  const toggleRow = useCallback(
    (id) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [],
  );
  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allIds = new Set(items.map((r) => rowKey(r)));
      const allSelected = items.every((r) => prev.has(rowKey(r)));
      return allSelected ? new Set() : allIds;
    });
  }, [items, rowKey]);

  /* -------------------- empty / scope guards ------------------------ */
  if (!clinicId) {
    return (
      <div
        data-testid="coord-no-clinic-scope"
        className="max-w-3xl mx-auto p-8 text-center text-slate-600"
      >
        <ShieldCheck className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-base font-semibold text-slate-900">{t('provider.coordDash.noScopeTitle')}</h1>
        <p className="mt-2 text-[12px]">
          {t('provider.coordDash.noScopeHelp')}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="coordinator-workspace" className="min-h-[calc(100vh-4rem)] bg-slate-50 flex flex-col">
      {/* HEADER — compact, sticky */}
      <header
        className="bg-white border-b border-slate-200 sticky top-0 z-20"
        data-testid="coord-header"
      >
        <div className="px-5 py-2 flex items-center gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                {t('provider.coordDash.coordWorkspace')}
              </div>
              <div className="text-[13px] font-semibold text-slate-800 truncate" data-testid="coord-clinic-name">
                {t('provider.coordDash.clinicPrefix', { id: clinicId })}
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
            <span data-testid="coord-user-name">{userName || '—'}</span>
            <button
              type="button"
              onClick={() => setReloadTick((t) => t + 1)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:border-slate-300 text-slate-700"
              data-testid="coord-refresh-btn"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              {t('provider.coordDash.refresh')}
            </button>
            <button
              type="button"
              onClick={() => setShowBulkImport(true)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-teal-200 bg-teal-50 hover:border-teal-300 text-teal-800"
              data-testid="bulk-import-patients-btn"
            >
              <FileSpreadsheet className="h-3 w-3" />
              {t('provider.leads.bulkImport.openAction')}
            </button>
          </div>
        </div>

        {/* View tabs — regrouped under 4 Level-2 parents
            (TREATX-COORDINATOR-IA-CONSOLIDATION-01). The previous 11
            flat tabs now sit inside caret dropdowns mirroring the
            Provider Admin pattern. All 11 underlying views remain
            untouched — only the surface hierarchy changes. */}
        <nav
          className="px-5 pb-0 border-t border-slate-100 flex items-center gap-1 overflow-x-clip"
          data-testid="coord-view-tabs"
          ref={navContainerRef}
        >
          {NAV_GROUPS.map((group) => {
            const hasSubmenu = group.childIds.length > 0;
            const isActiveSelf = view === group.id;
            const isActiveChild = hasSubmenu && group.childIds.includes(view);
            const isActive = isActiveSelf || isActiveChild;
            const isOpen = openNavSubmenu === group.id;

            return (
              <div key={group.id} className="relative flex items-stretch">
                <button
                  type="button"
                  onClick={() => setView(group.id)}
                  data-testid={hasSubmenu ? `coord-parent-${group.id}` : `coord-tab-${group.id}`}
                  className={`text-[12px] px-3 py-2 border-b-2 transition-colors -mb-px ${
                    isActive
                      ? 'border-slate-900 text-slate-900 font-semibold'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  } ${hasSubmenu ? 'pr-1' : ''}`}
                >
                  {t(group.labelKey)}
                  {summary?.buckets && group.id === 'cases' && (
                    <span className="ml-1.5 text-[10px] text-slate-400">{summary.buckets.all}</span>
                  )}
                </button>
                {hasSubmenu && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenNavSubmenu(isOpen ? null : group.id);
                    }}
                    data-testid={`coord-parent-${group.id}-caret`}
                    aria-expanded={isOpen}
                    aria-haspopup="menu"
                    aria-label={t('provider.coordDash.submenuAria', { label: t(group.labelKey) })}
                    className={`text-[12px] pl-0.5 pr-2 py-2 border-b-2 transition-colors -mb-px ${
                      isActive
                        ? 'border-slate-900 text-slate-900'
                        : 'border-transparent text-slate-400 hover:text-slate-700'
                    }`}
                  >
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                )}
                {hasSubmenu && isOpen && (
                  <div
                    role="menu"
                    data-testid={`coord-parent-${group.id}-submenu`}
                    className="absolute left-0 top-full mt-1 min-w-[200px] bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50"
                  >
                    {group.childIds.map((childId) => {
                      const child = VIEW_BY_ID[childId];
                      if (!child) return null;
                      const childActive = view === childId;
                      return (
                        <button
                          key={childId}
                          type="button"
                          role="menuitem"
                          onClick={() => { setView(childId); setOpenNavSubmenu(null); }}
                          data-testid={`coord-tab-${childId}`}
                          className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2 text-[12px] whitespace-nowrap transition-colors ${
                            childActive
                              ? 'bg-slate-100 text-slate-900 font-medium'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <span>{t(child.labelKey)}</span>
                          {summary?.buckets && childId === 'blockers' && (
                            <span className="text-[10px] text-rose-500">{summary.buckets.blocked}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </header>

      {/* COA-05 — Missing Documents Workspace (self-contained sub-view) */}
      {view === 'missing-docs' && <MissingDocumentsView />}

      {/* COA-06 — Missing Consents Workspace (self-contained sub-view) */}
      {view === 'missing-consents' && <MissingConsentsView />}

      {/* PROFILE-REQ-01B — Provider-Originated Profile Requests lens (reuses workspace shell) */}
      {view === 'profile-requests' && (
        <div className="px-5 py-4">
          <ProfileRequestsLens />
        </div>
      )}

      {/* M16B-04 — Coordinator Attestation lens (reuses workspace shell) */}
      {view === 'attestations' && (
        <div className="px-5 py-4">
          <CoordinatorAttestationLens />
        </div>
      )}

      {/* COA-02 — Cases inbox: lightweight filter strip (search · stage · chips) */}
      {view === 'cases' && (
        <div
          className="bg-white border-b border-slate-100 sticky top-[88px] z-10"
          data-testid="cases-inbox-filters"
        >
          <div className="px-5 pt-2 flex flex-wrap items-center gap-2">
            <div className="relative">
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('provider.coordDash.searchPh')}
                data-testid="cases-search-input"
                className="h-7 w-[260px] rounded border border-slate-200 bg-white px-2.5 text-[12px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-sky-400"
              />
            </div>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              data-testid="cases-stage-filter"
              className="h-7 rounded border border-slate-200 bg-white px-2 text-[12px] text-slate-700 focus:outline-none focus:border-sky-400"
            >
              <option value="__all__">{t('provider.coordDash.allStages')}</option>
              {stageOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {(searchTerm || stageFilter !== '__all__') && (
              <button
                type="button"
                onClick={() => { setSearchTerm(''); setStageFilter('__all__'); }}
                data-testid="cases-clear-filters"
                className="h-7 px-2 text-[11px] text-slate-500 hover:text-slate-800"
              >
                {t('provider.coordDash.clear')}
              </button>
            )}
          </div>
          <FilterChips
            chips={chips}
            activeId={bucket}
            onChange={setBucket}
            testIdPrefix="coord-filter-chip"
            resultLabel={t('provider.coordDash.resultCount', { count: visibleItems.length, total: summary?.buckets?.all ?? items.length })}
            rightSlot={
              <BulkBar
                selectedCount={selectedIds.size}
                actions={[
                  { id: 'open',  label: t('provider.coordDash.openAll'), onClick: () => {
                    Array.from(selectedIds).forEach((cid) => window.open(`/#/clinic/cases/${cid}/workflow`, '_blank'));
                  }},
                  { id: 'clear', label: t('provider.coordDash.clear'), onClick: () => setSelectedIds(new Set()) },
                ]}
              />
            }
          />
        </div>
      )}
      {view !== 'cases' && view !== 'missing-docs' && view !== 'missing-consents' && view !== 'profile-requests' && view !== 'attestations' && (
        <div className="px-5 py-2 border-b border-slate-100 bg-white flex items-center gap-2 text-[11px] text-slate-500">
          <span>{t('provider.coordDash.rowCount', { count: items.length })}</span>
          {summary?.buckets && (
            <>
              <span className="text-slate-300">·</span>
              <span>{t('provider.coordDash.casesTotal', { count: summary.buckets.all })}</span>
              <span className="text-slate-300">·</span>
              <span>{t('provider.coordDash.blocked', { count: summary.buckets.blocked })}</span>
              <span className="text-slate-300">·</span>
              <span>{t('provider.coordDash.overdue', { count: summary.buckets.overdue })}</span>
            </>
          )}
        </div>
      )}

      {/* GRID + DRAWER LAYOUT (skipped when missing-docs or missing-consents or profile-requests sub-view is active) */}
      {view !== 'missing-docs' && view !== 'missing-consents' && view !== 'profile-requests' && view !== 'attestations' && (
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0">
          <DataGrid
            testId="coord"
            columns={columns}
            rows={view === 'cases' ? visibleItems : items}
            rowKey={rowKey}
            selectedIds={selectedIds}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
            activeRowId={activeRow ? rowKey(activeRow) : null}
            onRowClick={setActiveRow}
            loading={loading}
            loadingMessage={t('provider.coordDash.loadingWorkspace')}
            emptyMessage={
              view === 'cases'
                ? (
                  <div data-testid="cases-empty-state" className="text-center py-10 max-w-md mx-auto">
                    <div className="text-[13px] font-semibold text-slate-700 mb-1">
                      {t('provider.coordDash.emptyCasesTitle')}
                    </div>
                    <div className="text-[11.5px] text-slate-500">
                      {t('provider.coordDash.emptyCasesHelp')}
                    </div>
                    {(searchTerm || stageFilter !== '__all__' || bucket !== 'all') && (
                      <button
                        type="button"
                        onClick={() => { setSearchTerm(''); setStageFilter('__all__'); setBucket('all'); }}
                        data-testid="cases-empty-clear-filters"
                        className="mt-3 inline-flex items-center text-[11px] px-2.5 py-1 rounded border border-slate-200 hover:border-slate-300 text-slate-700"
                      >
                        {t('provider.coordDash.clearAll')}
                      </button>
                    )}
                  </div>
                )
                : view === 'escalations'
                  ? t('provider.coordDash.emptyEscalations')
                  : view === 'overrides'
                    ? t('provider.coordDash.emptyOverrides')
                    : view === 'history'
                      ? t('provider.coordDash.emptyHistory')
                      : t('provider.coordDash.emptyRows')
            }
            selectable={view === 'cases' || view === 'requirements'}
            maxHeightClass="h-[calc(100vh-220px)]"
          />
        </div>

        {activeRow && (
          <DrawerForView
            view={view}
            row={activeRow}
            onClose={() => setActiveRow(null)}
            onJumpToCase={(caseId) => navigate(`/clinic/cases/${caseId}/workflow`)}
            nameLabel={nameLabel}
            t={t}
          />
        )}
      </div>
      )}

      <BulkLeadImport
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImported={() => setReloadTick((n) => n + 1)}
        reps={importReps}
      />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  DetailDrawer body per view                                          */
/* -------------------------------------------------------------------- */

function DrawerForView({ view, row, onClose, onJumpToCase, nameLabel, t }) {
  const caseId = row.case_id || row.assignee_id || row.override_id || row.revision_id;
  const shortId = (row.case_id || '').slice(-6);
  const title =
    view === 'cases'        ? `${row.patient_name} · ${row.treatment_name || row.treatment_slug || ''}`
    : view === 'requirements' ? row.title
    : view === 'blockers'   ? row.title
    : view === 'escalations' ? row.item_name
    : view === 'overrides'  ? row.op
    : view === 'assignments' ? `${row.role}${row.is_unassigned ? t('provider.coordDash.unassignedSuffix') : ''}`
    : row.action || t('provider.coordDash.titleRevision');
  const eyebrow =
    view === 'cases'        ? t('provider.coordDash.eyebrowCase', { id: shortId })
    : view === 'requirements' ? t('provider.coordDash.eyebrowRequirement', { id: shortId })
    : view === 'blockers'   ? t('provider.coordDash.eyebrowBlocker', { id: shortId })
    : view === 'escalations' ? t('provider.coordDash.eyebrowEscalation', { id: shortId })
    : view === 'overrides'  ? t('provider.coordDash.eyebrowOverride', { id: shortId })
    : view === 'assignments' ? row.role
    : t('provider.coordDash.eyebrowRevision', { id: shortId });

  const footer = (view === 'cases' || view === 'requirements' || view === 'blockers' || view === 'escalations' || view === 'overrides' || view === 'history') ? (
    <div className="flex items-center justify-between">
      <span className="text-[10.5px] text-slate-500 font-mono truncate">{caseId}</span>
      {row.case_id && (
        <button
          type="button"
          onClick={() => onJumpToCase(row.case_id)}
          className="text-[11px] px-2 py-1 bg-slate-900 text-white rounded hover:bg-slate-800 inline-flex items-center gap-1"
          data-testid="coord-drawer-open-case"
        >
          {t('provider.coordDash.openCaseFooter')} <ExternalLink className="h-3 w-3" />
        </button>
      )}
    </div>
  ) : null;

  return (
    <DetailDrawer
      open
      onClose={onClose}
      title={title}
      eyebrow={eyebrow}
      footer={footer}
      testId="coord-drawer"
    >
      {view === 'cases' && (
        <>
          <DrawerField label={t('provider.coordDash.fldPatient')}       value={row.patient_name || '—'} testId="d-patient" />
          <DrawerField label={t('provider.coordDash.fldCountry')}       value={row.country || '—'} />
          <DrawerField label={t('provider.coordDash.fldTreatment')}     value={row.treatment_name || row.treatment_slug || '—'} />
          <DrawerField label={t('provider.coordDash.fldJourneyStep')}   value={row.journey_step || '—'} />
          <DrawerField label={t('provider.coordDash.fldStatus')}        value={row.status || '—'} />
          <DrawerField label={t('provider.coordDash.fldPendingItems')}  value={row.outstanding} />
          <DrawerField label={t('provider.coordDash.fldBlocked')}       value={row.blocked} />
          <DrawerField label={t('provider.coordDash.fldOverdue')}       value={row.overdue} />
          <DrawerField label={t('provider.coordDash.fldMissingDocs')}   value={row.missing_docs} />
          <DrawerField label={t('provider.coordDash.fldMissingConsents')} value={row.missing_consents} />
          <DrawerField label={t('provider.coordDash.fldHasPlan')}       value={row.has_plan ? t('provider.coordDash.yes') : t('provider.coordDash.no')} />
          <DrawerField label={t('provider.coordDash.fldOverrideCount')} value={row.override_count} />
          <DrawerField label={t('provider.coordDash.fldCoordinator')}   value={row.assigned_coordinator_id || '—'} />
          <DrawerField label={t('provider.coordDash.fldDoctor')}        value={row.assigned_doctor_id || '—'} />
          <DrawerField label={t('provider.coordDash.fldSalesRep')}      value={row.assigned_sales_id || '—'} />
          <DrawerField label={t('provider.coordDash.fldAfterSales')}    value={row.assigned_after_sales_id || '—'} />
          <DrawerField label={t('provider.coordDash.fldLastActivity')}  value={fmtRel(row.last_activity)} />
        </>
      )}
      {view === 'requirements' && (
        <>
          <DrawerField label={t('provider.coordDash.fldCase')}     value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label={t('provider.coordDash.fldPatient')}  value={row.patient_name || '—'} />
          <DrawerField label={t('provider.coordDash.fldKind')}     value={row.requirement_type || row.kind} />
          <DrawerField label={t('provider.coordDash.fldSource')}   value={row.source || '—'} />
          <DrawerField label={t('provider.coordDash.fldOwner')}    value={row.default_role || '—'} />
          <DrawerField label={t('provider.coordDash.fldStatus')}   value={row.satisfaction_status || '—'} />
          <DrawerField label={t('provider.coordDash.fldDue')}      value={fmtDate(row.due_at)} />
          {row.satisfied_at && <DrawerField label={t('provider.coordDash.fldSatisfied')} value={fmtRel(row.satisfied_at)} />}
          {row.satisfied_via && <DrawerField label={t('provider.coordDash.fldVia')}       value={row.satisfied_via} />}
          {row.satisfied_by  && <DrawerField label={t('provider.coordDash.fldBy')}        value={row.satisfied_by} />}
          {row.legal_document_id && <DrawerField label={t('provider.coordDash.fldLegalDoc')} value={`${row.legal_document_id} v${row.legal_document_version || '—'}`} />}
          {row.consent_content_key && <DrawerField label={t('provider.coordDash.fldConsent')} value={`${row.consent_content_key} v${row.consent_content_version || '—'}`} />}
          {row.requirement_id && <DrawerField label={t('provider.coordDash.fldRequirementId')} value={row.requirement_id} />}
        </>
      )}
      {view === 'blockers' && (
        <>
          <DrawerField label={t('provider.coordDash.fldCase')}    value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label={t('provider.coordDash.fldPatient')} value={row.patient_name || '—'} />
          <DrawerField label={t('provider.coordDash.fldKind')}    value={row.requirement_type || row.kind} />
          <DrawerField label={t('provider.coordDash.fldOwner')}   value={row.owner || '—'} />
          <DrawerField label={t('provider.coordDash.fldDue')}     value={fmtDate(row.due_at)} />
          <DrawerField label={t('provider.coordDash.fldAge')}     value={row.age_days > 0 ? `${row.age_days}d` : t('provider.coordDash.ageToday')} />
          <div className="pt-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{t('provider.coordDash.fldReasons')}</div>
            <div className="flex flex-wrap gap-1">
              {(row.reasons || []).map((r) => <Pill key={r} tone="rose">{r}</Pill>)}
            </div>
          </div>
        </>
      )}
      {view === 'overrides' && (
        <>
          <DrawerField label={t('provider.coordDash.fldCase')}      value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label={t('provider.coordDash.fldTreatment')} value={row.treatment_slug || '—'} />
          <DrawerField label={t('provider.coordDash.fldOperation')} value={row.op} />
          <DrawerField label={t('provider.coordDash.fldActor')}     value={row.actor || '—'} />
          <DrawerField label={t('provider.coordDash.fldCreated')}   value={fmtRel(row.created_at)} />
          <DrawerField label={t('provider.coordDash.fldStatus')}    value={row.undone ? t('provider.coordDash.statusUndone') : t('provider.coordDash.statusActive')} />
          {row.undone_at && <DrawerField label={t('provider.coordDash.fldUndoneAt')} value={fmtRel(row.undone_at)} />}
          {row.undone_by && <DrawerField label={t('provider.coordDash.fldUndoneBy')} value={row.undone_by} />}
          <div className="pt-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{t('provider.coordDash.fldArgs')}</div>
            <pre className="text-[10.5px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto font-mono text-slate-700">
              {JSON.stringify(row.args || {}, null, 2)}
            </pre>
          </div>
        </>
      )}
      {view === 'assignments' && (
        <>
          <DrawerField label={t('provider.coordDash.fldRole')}             value={row.role} />
          {!row.is_unassigned && <DrawerField label={t('provider.coordDash.fldAssigneeId')} value={row.assignee_id || '—'} />}
          <DrawerField label={t('provider.coordDash.fldOpenCases')}       value={row.open_cases} />
          <DrawerField label={t('provider.coordDash.fldBlockedCases')}    value={row.blocked} />
          <DrawerField label={t('provider.coordDash.fldOverdueCases')}    value={row.overdue} />
          <DrawerField label={t('provider.coordDash.fldPendingItems')} value={row.outstanding_items} />
        </>
      )}
      {view === 'escalations' && (
        <>
          <DrawerField label={t('provider.coordDash.fldCase')}       value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label={t('provider.coordDash.fldPatient')}    value={row.patient_name || '—'} />
          <DrawerField label={t('provider.coordDash.fldTreatment')}  value={row.treatment_name || row.treatment_slug || '—'} />
          <DrawerField label={t('provider.coordDash.fldItem')}       value={row.item_name || '—'} />
          <DrawerField label={t('provider.coordDash.fldSource')}     value={row.source || '—'} />
          <DrawerField label={t('provider.coordDash.fldOwnerRole')}  value={row.default_role || '—'} />
          <DrawerField label={t('provider.coordDash.fldEscalatedBy')} value={nameLabel ? nameLabel(row.escalated_by) : (row.escalated_by || '—')} />
          <DrawerField label={t('provider.coordDash.fldEscalatedAt')} value={fmtRel(row.escalated_at)} />
          <DrawerField label={t('provider.coordDash.fldStatus')}     value={row.is_satisfied ? t('provider.coordDash.statusSatisfied') : (row.is_overdue ? t('provider.coordDash.statusOverdue') : t('provider.coordDash.statusActive'))} />
          {row.due_at && <DrawerField label={t('provider.coordDash.fldDue')} value={fmtDate(row.due_at)} />}
        </>
      )}
      {view === 'history' && (
        <>
          <DrawerField label={t('provider.coordDash.fldCase')}   value={`#${(row.case_id || '').slice(-6)}`} />
          <DrawerField label={t('provider.coordDash.fldAction')} value={row.action || '—'} />
          <DrawerField label={t('provider.coordDash.fldActor')}  value={row.actor || '—'} />
          <DrawerField label={t('provider.coordDash.fldWhen')}   value={fmtRel(row.created_at)} />
          {row.before && (
            <div className="pt-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{t('provider.coordDash.fldBefore')}</div>
              <pre className="text-[10.5px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto font-mono text-slate-700">
                {JSON.stringify(row.before, null, 2)}
              </pre>
            </div>
          )}
          {row.after && (
            <div className="pt-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{t('provider.coordDash.fldAfter')}</div>
              <pre className="text-[10.5px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto font-mono text-slate-700">
                {JSON.stringify(row.after, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}
    </DetailDrawer>
  );
}
